import { inject, Service } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { dateRangeValidator } from "../../core/validators";
import { Constants } from "../constants/constants";
import { ItemAction } from "../models/data-table-model";

export type GovListPageConfig = {
  tableConfig: string;
  permission: number;
  childPermission?: number;
  translationKey: string;
  routeKey: string;
  apiPrefix: string;
};

export type GovEditorPageConfig = {
  formConfig: string;
  form: FormGroup;
  translationKey: string;
  routeKey: string;
};

export type GovMilestoneEditorPageConfig = GovEditorPageConfig & {
  apiPrefix: string;
  taskType: number;
  tasksTableConfig: string;
};

export type GovTaskProgressPageConfig = GovEditorPageConfig & {
  apiPrefix: string;
};

export type GovTrackEditorPageConfig = GovEditorPageConfig & {
  apiPrefix: string;
  milestoneType: number;
  milestonesTableConfig: string;
};

export const GOV_IN_REVIEW_RULES: ItemAction[] = [
  {
    propertyName: "status",
    compareWith: Constants.OBJECT_STATUS.IN_REVIEW,
    operator: "equal",
  },
];

@Service()
export class GovProjectsService {
  private readonly _formBuilder = inject(FormBuilder);

  public trackListPageConfig(trackType: number): GovListPageConfig | null {
    switch (trackType) {
      case Constants.GOV_TRACKS.AI:
        return {
          tableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TRACK_LIST,
          permission: Constants.CONSTANT_PERMISSIONS.AI_PROJECT_TRACK,
          childPermission: Constants.CONSTANT_PERMISSIONS.AI_PROJECT_MILESTONE,
          translationKey: "ai_projects",
          routeKey: "ai-projects",
          apiPrefix: "ai-project",
        };
      default:
        return null;
    }
  }

  public trackEditorPageConfig(trackType: number, isViewMode: boolean): GovTrackEditorPageConfig | null {
    switch (trackType) {
      case Constants.GOV_TRACKS.AI:
        return {
          formConfig: Constants.VALIDATORS_CONF_KEY.AI_PROJECT_TRACK_FORM,
          form: this._aiTrackForm(isViewMode),
          translationKey: "ai_projects",
          routeKey: "ai-projects",
          apiPrefix: "ai-project",
          milestoneType: Constants.GOV_MILESTONES.AI,
          milestonesTableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TRACK_EDIT_MILESTONE_LIST,
        };
      default:
        return null;
    }
  }

  public milestoneListPageConfig(milestoneType: number): GovListPageConfig | null {
    switch (milestoneType) {
      case Constants.GOV_MILESTONES.AI:
        return {
          tableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_MILESTONE_LIST,
          permission: Constants.CONSTANT_PERMISSIONS.AI_PROJECT_MILESTONE,
          childPermission: Constants.CONSTANT_PERMISSIONS.AI_PROJECT_TASK,
          translationKey: "ai_projects",
          routeKey: "ai-projects",
          apiPrefix: "ai-project",
        };
      default:
        return null;
    }
  }

  public milestoneEditorPageConfig(milestoneType: number, isViewMode: boolean): GovMilestoneEditorPageConfig | null {
    switch (milestoneType) {
      case Constants.GOV_MILESTONES.AI:
        return {
          formConfig: Constants.VALIDATORS_CONF_KEY.AI_PROJECT_MILESTONE_FORM,
          form: this._aiMilestoneForm(isViewMode),
          translationKey: "ai_projects",
          routeKey: "ai-projects",
          apiPrefix: "ai-project",
          taskType: Constants.GOV_TASKS.AI,
          tasksTableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_MILESTONE_EDIT_TASK_LIST,
        };
      default:
        return null;
    }
  }

  public taskEditorPageConfig(taskType: number, isViewMode: boolean): GovEditorPageConfig | null {
    switch (taskType) {
      case Constants.GOV_TASKS.AI:
        return {
          formConfig: Constants.VALIDATORS_CONF_KEY.AI_PROJECT_TASK_FORM,
          form: this._aiTaskForm(isViewMode),
          translationKey: "ai_projects",
          routeKey: "ai-projects",
        };
      default:
        return null;
    }
  }

  public taskProgressPageConfig(taskType: number, isViewMode: boolean): GovTaskProgressPageConfig | null {
    switch (taskType) {
      case Constants.GOV_TASKS.AI:
        return {
          formConfig: Constants.VALIDATORS_CONF_KEY.AI_PROJECT_TASK_FORM,
          form: this._aiTaskProgressForm(isViewMode),
          translationKey: "ai_projects",
          routeKey: "ai-projects",
          apiPrefix: "ai-project",
        };
      default:
        return null;
    }
  }

  public taskListPageConfig(taskType: number): GovListPageConfig | null {
    switch (taskType) {
      case Constants.GOV_TASKS.AI:
        return {
          tableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TASK_LIST,
          permission: Constants.CONSTANT_PERMISSIONS.AI_PROJECT_TASK,
          translationKey: "ai_projects",
          routeKey: "ai-projects",
          apiPrefix: "ai-project",
        };
      default:
        return null;
    }
  }

  private _aiTrackForm(isViewMode: boolean): FormGroup {
    return this._formBuilder.group({
      id: null,
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      dscAE: { value: null, disabled: isViewMode },
      entityId: { value: null, disabled: isViewMode },
      trackManagerId: { value: null, disabled: isViewMode },
      trackCoordinatorId: { value: null, disabled: isViewMode },
      workflowApproval: { value: null, disabled: isViewMode },
      entryClassification: { value: null, disabled: isViewMode },
      status: Constants.OBJECT_STATUS.ACTIVE,
      milestones: { value: [], disabled: isViewMode },
    });
  }

  private _aiMilestoneForm(isViewMode: boolean): FormGroup {
    return this._formBuilder.group(
      {
        id: null,
        fakeId: null,
        trackId: null,
        nameAE: { value: null, disabled: isViewMode },
        nameEN: { value: null, disabled: isViewMode },
        dscEN: { value: null, disabled: isViewMode },
        dscAE: { value: null, disabled: isViewMode },
        startDate: { value: null, disabled: isViewMode },
        endDate: { value: null, disabled: isViewMode },
        weight: { value: null, disabled: isViewMode },
        status: Constants.OBJECT_STATUS.ACTIVE,
        tasks: { value: [], disabled: isViewMode },
        mainAchievements: { value: [], disabled: isViewMode },
        recommendations: { value: [], disabled: isViewMode },
      },
      {
        validators: Validators.compose([dateRangeValidator("startDate", "endDate")]),
      }
    );
  }

  private _aiTaskProgressForm(isViewMode: boolean): FormGroup {
    return this._formBuilder.group({
      id: null,
      milestoneId: null,
      parentTaskId: null,
      nameAE: { value: null, disabled: true },
      nameEN: { value: null, disabled: true },
      dscEN: { value: null, disabled: true },
      dscAE: { value: null, disabled: true },
      startDate: { value: null, disabled: true },
      endDate: { value: null, disabled: true },
      weight: { value: null, disabled: true },
      taskType: { value: null, disabled: true },
      entityIds: { value: [], disabled: true },
      target: { value: null, disabled: true },
      progressActual: { value: null, disabled: isViewMode },
      attachments: { value: [], disabled: isViewMode },
      comments: { value: [], disabled: isViewMode },
      status: Constants.OBJECT_STATUS.ACTIVE,
    });
  }

  private _aiTaskForm(isViewMode: boolean): FormGroup {
    return this._formBuilder.group(
      {
        id: null,
        fakeId: null,
        milestoneId: null,
        parentTaskId: null,
        nameAE: { value: null, disabled: isViewMode },
        nameEN: { value: null, disabled: isViewMode },
        dscEN: { value: null, disabled: isViewMode },
        dscAE: { value: null, disabled: isViewMode },
        startDate: { value: null, disabled: isViewMode },
        endDate: { value: null, disabled: isViewMode },
        weight: { value: null, disabled: isViewMode },
        taskType: { value: null, disabled: isViewMode },
        entityIds: { value: [], disabled: isViewMode },
        target: { value: null, disabled: isViewMode },
        status: Constants.OBJECT_STATUS.ACTIVE,
      },
      {
        validators: Validators.compose([dateRangeValidator("startDate", "endDate")]),
      }
    );
  }
}
